import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { EScheduleService } from "@store/new-e-schedule/new-e-schedule.service";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import {
  DOWNLOAD_CI_REPORT_FROM_URL,
  GET_CI_REPORT_AGENT_LIST,
  GET_CI_REPORT_BRANCH_LIST,
  GET_CI_REPORT_CC_AGENT_LIST,
  GET_CI_REPORT_FILTER_OPTIONS,
  GET_CI_REPORT_SEARCH,
  GET_FILTERED_CI_REPORT_AGENT_LIST,
} from "./ci-report.action";
import {
  BranchList,
  CIReportFilterOptions,
  SearchReport,
} from "./ci-report.interface";
import { CIReportService } from "./ci-report.service";

interface CIReportModel {
  reportFilterOptions: CIReportFilterOptions | null;
  searchReport: SearchReport | null;
  branchList: BranchList[];
  agentList: BranchList[] | null;
}

@State<CIReportModel>({
  name: "CIReportState",
  defaults: {
    reportFilterOptions: null,
    searchReport: null,
    branchList: [],
    agentList: [],
  },
})
@Injectable()
export class CIReportState {
  constructor(
    private readonly ciReportService: CIReportService,
    private readonly escheduleService: EScheduleService,
  ) {}

  @Selector()
  public static agentList(state: CIReportModel): BranchList[] {
    return state.agentList ? state.agentList : [];
  }

  @Selector()
  public static branchList(state: CIReportModel): BranchList[] {
    return state.branchList ? state.branchList : [];
  }

  @Action(GET_CI_REPORT_SEARCH)
  public getSearchList(
    { patchState, dispatch }: StateContext<CIReportModel>,
    { payload }: GET_CI_REPORT_SEARCH,
  ): Observable<unknown> {
    return this.ciReportService.reportSearch(payload).pipe(
      map((res) => {
        patchState({
          searchReport: res.data,
        });
        return dispatch(new DOWNLOAD_CI_REPORT_FROM_URL(payload));
      }),
    );
  }

  @Action(GET_CI_REPORT_FILTER_OPTIONS)
  public getReportFilterOptions(
    { patchState }: StateContext<CIReportModel>,
    { payload }: GET_CI_REPORT_FILTER_OPTIONS,
  ): Observable<void> {
    return this.ciReportService.getReportTypeFilterOptions(payload).pipe(
      map((res) => {
        patchState({
          reportFilterOptions: res.data,
        });
      }),
    );
  }

  @Action(DOWNLOAD_CI_REPORT_FROM_URL)
  public getDownloadReportUrl(
    _ctx: StateContext<CIReportModel>,
    { payload }: DOWNLOAD_CI_REPORT_FROM_URL,
  ): Window | null {
    return this.ciReportService.downloadReport(payload);
  }

  @Action(GET_CI_REPORT_BRANCH_LIST)
  public getBranchList(
    { patchState }: StateContext<CIReportModel>,
    { payload }: GET_CI_REPORT_BRANCH_LIST,
  ): Observable<void> {
    return this.ciReportService.getBranchList(payload).pipe(
      map((res) => {
        patchState({
          branchList: res?.data ? res.data?.userBranchList : [],
        });
      }),
    );
  }

  @Action(GET_CI_REPORT_AGENT_LIST)
  public getAgentList(
    { patchState }: StateContext<CIReportModel>,
    { payload }: GET_CI_REPORT_AGENT_LIST,
  ): Observable<void> {
    return this.ciReportService.getAgentList(payload).pipe(
      map((res) => {
        if (res?.data) {
          let agentList = res.data?.agentList;
          // Remove - All - from BDM Admin
          agentList = agentList.filter((agent) => agent.agentCode !== "-1");
          patchState({
            agentList: agentList,
          });
        }
      }),
    );
  }

  @Action(GET_FILTERED_CI_REPORT_AGENT_LIST)
  public getFilteredAgentList(
    { patchState }: StateContext<CIReportModel>,
    { payload }: GET_FILTERED_CI_REPORT_AGENT_LIST,
  ): Observable<void> {
    return this.ciReportService.getFilteredAgentList(payload).pipe(
      map((res) => {
        patchState({
          agentList: res?.data ? res.data?.agentList : null,
        });
      }),
    );
  }

  @Action(GET_CI_REPORT_CC_AGENT_LIST)
  public getCallCenterAgentList(
    { patchState }: StateContext<CIReportModel>,
    { payload }: GET_CI_REPORT_CC_AGENT_LIST,
  ): Observable<void> {
    return this.escheduleService.getCallCenterAgentList(payload).pipe(
      map((res) => {
        if (res?.data) {
          patchState({
            agentList: res?.data ? res.data?.agentList : [],
          });
        }
      }),
    );
  }
}
